import cn from 'classnames';
import { useCallback, useMemo, JSX } from 'react';

import { useCheckoutContext } from '@zooplus/checkout-state';
import {
  ApplePayModernizeCheckoutButton,
  BraintreeAuthorizationButton,
  PayPalModernizeCheckoutButton,
} from '@zooplus/payment-frontend';
import { P2 } from '@zooplus/ui-components';

import FeedbackMessage from '@components/common/notifications/feedback-message';
import SepaExpressButton from '@components/preview/payment-component/sepa/sepa-express-button';
import CheckoutPlaceOrderButton from '@components/preview/preview-place-order-button/checkout-place-order-button';
import { useTranslation } from '@hooks/use-translations';
import { MESSAGE_PLACEMENT, CHECKOUT_STATE_PROJECT_ID, PAYMENT_METHOD } from '@utilities/constants';
import { HTMLParser } from '@utilities/html-parser';
import { isMobileViewport } from '@utilities/utils';

import styles from './preview-place-order-button.module.scss';

const PreviewPlaceOrderButton = () => {
  const { t } = useTranslation();
  const { checkoutState, isLoading } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const preferredPayment = checkoutState?.payment?.preferredPayment;
  const sepaExpressVerificationRequired =
    checkoutState?.payment?.sepaExpressButton?.sepaExpressVerificationRequired ?? false;
  const customerHash = checkoutState?.stateAttributes.customerHash ?? '';
  const orderPreviewGrandTotal = checkoutState?.stateAttributes.orderPreviewGrandTotal ?? '';
  const csrfToken = checkoutState?.stateAttributes.csrfToken ?? '';
  const orderNowButtonText = t('checkout.preview.button.order.now');

  const isPlaceOrderButtonDisabled = useMemo(() => {
    const { stateMessages, delivery, placeOrderConfig } = checkoutState || {};
    const { selectedDeliveryService } = delivery || {};
    const { blockingReason } = selectedDeliveryService || {};

    const isConfigInvalid = !placeOrderConfig?.placeOrderButtonEnabled || !preferredPayment;
    const isDeliveryInvalid = !selectedDeliveryService || !!blockingReason;

    const hasErrors =
      (stateMessages?.customerMessages && stateMessages.customerMessages.length > 0) ||
      !!stateMessages?.addressMessages;

    return isConfigInvalid || isDeliveryInvalid || hasErrors;
  }, [checkoutState, preferredPayment]);

  const renderPaymentButton = useCallback(
    (button: JSX.Element) => {
      if (isPlaceOrderButtonDisabled) {
        return (
          <CheckoutPlaceOrderButton
            customerHash={customerHash}
            orderPreviewGrandTotal={orderPreviewGrandTotal}
            csrfToken={csrfToken}
            isDisabled={isPlaceOrderButtonDisabled}
            isLoading={isLoading ?? false}
            buttonText={orderNowButtonText}
          />
        );
      }
      return button;
    },
    [isPlaceOrderButtonDisabled],
  );

  const renderCheckoutPaymentButton = useCallback(() => {
    const { DIRECT_DEBIT_SEPA, PAYPAL, CREDIT_CARD, APPLE_PAY, BRAINTREE } = PAYMENT_METHOD;
    const { paymentMethods, paypalButton, applePayButton } = checkoutState?.payment ?? {};
    const { paymentMethodShortName } = preferredPayment ?? {};
    const creditCardPaymentMethod = paymentMethods?.find(
      (pm) => pm.paymentMethodShortName === CREDIT_CARD,
    );

    const checkoutPlaceOrderButton = (
      <CheckoutPlaceOrderButton
        customerHash={customerHash}
        orderPreviewGrandTotal={orderPreviewGrandTotal}
        csrfToken={csrfToken}
        isDisabled={isPlaceOrderButtonDisabled}
        isLoading={isLoading ?? false}
        buttonText={orderNowButtonText}
      />
    );

    if (paymentMethodShortName === DIRECT_DEBIT_SEPA) {
      return sepaExpressVerificationRequired ? <SepaExpressButton /> : <></>;
    } else if (
      paymentMethodShortName === CREDIT_CARD &&
      creditCardPaymentMethod?.details?.paymentSetupInfo?.clientInfo?.pspName === BRAINTREE
    ) {
      return renderPaymentButton(<BraintreeAuthorizationButton />);
    } else if (paymentMethodShortName === PAYPAL && !!paypalButton) {
      return renderPaymentButton(<PayPalModernizeCheckoutButton />);
    } else if (paymentMethodShortName === APPLE_PAY && !!applePayButton) {
      return renderPaymentButton(<ApplePayModernizeCheckoutButton />);
    } else {
      return checkoutPlaceOrderButton;
    }
  }, [
    csrfToken,
    customerHash,
    isLoading,
    isPlaceOrderButtonDisabled,
    orderNowButtonText,
    orderPreviewGrandTotal,
    preferredPayment?.paymentMethodShortName,
    sepaExpressVerificationRequired,
  ]);

  return (
    <div
      className={cn(styles.buttonSectionWrapper, 'pp-mb-l')}
      data-testid="preview-place-order-button">
      <FeedbackMessage
        placement={
          isMobileViewport()
            ? MESSAGE_PLACEMENT.PLACE_ORDER_BUTTON_POSITION_ABOVE_MOBILE
            : MESSAGE_PLACEMENT.PLACE_ORDER_BUTTON_POSITION_ABOVE
        }
        customStyle={styles.placeOrderErrorMessage}
      />
      {renderCheckoutPaymentButton()}
      <HTMLParser
        customStyle={styles.legalText}
        tagName={P2}
        content={t('checkout.preview.agb.privacy.label')}
      />
    </div>
  );
};

export default PreviewPlaceOrderButton;
